import { useQuery } from "@tanstack/react-query";
import type { StyleSpecification } from "maplibre-gl";
import maplibregl from "maplibre-gl";
import { Protocol } from "pmtiles";
import * as React from "react";
import {
    BASEMAP_MANIFEST_URL,
    buildSelfHostedMapStyle,
    MAPTILER_STYLE_URL,
    parseBasemapManifest,
} from "./map-style";

const SELF_HOSTED_TILES_KEY = "sp-self-hosted-map-tiles";

declare global {
    interface Window {
        crmSelfHostedTiles: {
            enable: () => void;
            disable: () => void;
        };
    }
}

window.crmSelfHostedTiles = {
    enable: () => {
        window.localStorage.setItem(SELF_HOSTED_TILES_KEY, "true");
        window.location.reload();
    },
    disable: () => {
        window.localStorage.setItem(SELF_HOSTED_TILES_KEY, "false");
        window.location.reload();
    },
};

const selfHostedTilesEnabled = (): boolean => {
    try {
        return window.localStorage.getItem(SELF_HOSTED_TILES_KEY) === "true";
    } catch {
        return false;
    }
};

let pmtilesProtocolRegistered = false;

const ensurePmtilesProtocol = (): void => {
    if (pmtilesProtocolRegistered) {
        return;
    }
    maplibregl.addProtocol("pmtiles", new Protocol().tile);
    pmtilesProtocolRegistered = true;
};

export const useMapStyle = (): string | StyleSpecification => {
    const enabled = selfHostedTilesEnabled();
    const manifestQuery = useQuery({
        queryKey: ["basemapManifest"],
        enabled,
        staleTime: Infinity,
        queryFn: async () => {
            const response = await fetch(BASEMAP_MANIFEST_URL);
            if (!response.ok) {
                throw new Error(`Basemap manifest request failed: ${response.status}`);
            }
            return parseBasemapManifest(await response.json());
        },
    });
    const manifest = manifestQuery.data;
    return React.useMemo(() => {
        if (!enabled || !manifest) {
            return MAPTILER_STYLE_URL;
        }
        ensurePmtilesProtocol();
        return buildSelfHostedMapStyle(manifest.tiles);
    }, [enabled, manifest]);
};
